import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { WarehouseAmountMode } from '../types/warehouse';
import { WarehouseEntry, WarehouseEntryType } from '../types/warehouse';

const WAREHOUSE_COLLECTION = 'warehouse-entries';

const toDateValue = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const normalizeNumber = (value: number | string | undefined): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
};

const inferAmountMode = (unit: string, explicit?: WarehouseAmountMode): WarehouseAmountMode => {
  if (explicit) return explicit;
  const u = String(unit || 'g').trim().toLowerCase();
  return u === 'g' ? 'per1000g' : 'baseUnit';
};

const computeTotalAmount = (quantity: number, unitPrice: number, mode: WarehouseAmountMode): number => {
  if (mode === 'per1000g') return (quantity / 1000) * unitPrice;
  return quantity * unitPrice;
};

const normalizeEntry = (raw: any): WarehouseEntry => {
  const quantity = normalizeNumber(raw.quantity);
  const rawQuantity =
    raw.rawQuantity === undefined || raw.rawQuantity === null || raw.rawQuantity === ''
      ? undefined
      : normalizeNumber(raw.rawQuantity);
  const unitPrice = normalizeNumber(raw.unitPrice);
  const unit = String(raw.unit || 'g').trim();
  const amountMode = (raw.amountMode as WarehouseAmountMode) || inferAmountMode(unit);
  const hasStoredTotal = raw.totalAmount !== undefined && raw.totalAmount !== null && raw.totalAmount !== '';
  const totalAmount = hasStoredTotal
    ? normalizeNumber(raw.totalAmount)
    : computeTotalAmount(quantity, unitPrice, amountMode);

  return {
    id: raw.id,
    month: String(raw.month || ''),
    coffeeTypeId: String(raw.coffeeTypeId || ''),
    coffeeTypeName: String(raw.coffeeTypeName || ''),
    unit,
    quantity,
    rawQuantity,
    unitPrice,
    totalAmount,
    amountMode,
    entryType: (raw.entryType as WarehouseEntryType) || 'import',
    storeLocationId: raw.storeLocationId || undefined,
    storeName: raw.storeName || undefined,
    partnerId: raw.partnerId || undefined,
    partnerName: raw.partnerName || undefined,
    partnerTransactionId: raw.partnerTransactionId || undefined,
    exportTargetType: raw.exportTargetType || undefined,
    purchaseItemId: raw.purchaseItemId || undefined,
    notes: raw.notes || '',
    createdBy: raw.createdBy || '',
    createdAt: toDateValue(raw.createdAt),
    updatedAt: toDateValue(raw.updatedAt),
  };
};

export const warehouseService = {
  async getAllEntries(): Promise<WarehouseEntry[]> {
    const q = query(collection(db, WAREHOUSE_COLLECTION), orderBy('month', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizeEntry({ id: item.id, ...item.data() }));
  },

  async getEntriesByMonth(month: string): Promise<WarehouseEntry[]> {
    const q = query(
      collection(db, WAREHOUSE_COLLECTION),
      where('month', '==', month),
      orderBy('coffeeTypeName', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizeEntry({ id: item.id, ...item.data() }));
  },

  async addEntry(
    data: Omit<WarehouseEntry, 'id' | 'createdAt' | 'updatedAt' | 'totalAmount'>
  ): Promise<WarehouseEntry> {
    const quantity = normalizeNumber(data.quantity);
    const unitPrice = normalizeNumber(data.unitPrice);
    const unit = String(data.unit || 'g').trim();
    const amountMode = data.amountMode || inferAmountMode(unit);
    const totalAmount = computeTotalAmount(quantity, unitPrice, amountMode);

    const payload = {
      ...data,
      quantity,
      unitPrice,
      totalAmount,
      amountMode,
      entryType: data.entryType || 'import',
      coffeeTypeName: String(data.coffeeTypeName || '').trim(),
      unit,
      notes: (data.notes || '').trim(),
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    const docRef = await addDoc(collection(db, WAREHOUSE_COLLECTION), payload);
    return normalizeEntry({ id: docRef.id, ...payload });
  },

  // Tạo bản ghi xuất kho khi cơ sở lấy hàng từ kho tổng
  async addExportEntry(data: {
    month: string;
    coffeeTypeId: string;
    coffeeTypeName: string;
    quantity: number; // dương (gram nếu unit g, hoặc theo đơn vị gốc)
    unitPrice: number; // đ/kg (per1000g) hoặc đ/1 đơn vị (baseUnit)
    unit?: string;
    amountMode?: WarehouseAmountMode;
    storeLocationId: string;
    storeName: string;
    purchaseItemId?: string;
    notes?: string;
    createdBy?: string;
  }): Promise<WarehouseEntry> {
    const unit = String(data.unit || 'g').trim();
    const amountMode = data.amountMode ?? inferAmountMode(unit);
    return this.addEntry({
      month: data.month,
      coffeeTypeId: data.coffeeTypeId,
      coffeeTypeName: data.coffeeTypeName,
      unit,
      amountMode,
      quantity: -Math.abs(data.quantity),
      unitPrice: data.unitPrice,
      entryType: 'export',
      exportTargetType: 'store',
      storeLocationId: data.storeLocationId,
      storeName: data.storeName,
      purchaseItemId: data.purchaseItemId,
      notes: data.notes || '',
      createdBy: data.createdBy || '',
    });
  },

  async addPartnerExportEntry(data: {
    month: string;
    coffeeTypeId: string;
    coffeeTypeName: string;
    quantity: number; // số lượng gram (dương, sẽ lưu âm)
    unitPrice: number; // đ/1000g
    partnerId: string;
    partnerName: string;
    partnerTransactionId: string;
    notes?: string;
    createdBy?: string;
  }): Promise<WarehouseEntry> {
    return this.addEntry({
      month: data.month,
      coffeeTypeId: data.coffeeTypeId,
      coffeeTypeName: data.coffeeTypeName,
      unit: 'g',
      quantity: -Math.abs(data.quantity),
      unitPrice: data.unitPrice,
      entryType: 'export',
      exportTargetType: 'partner',
      partnerId: data.partnerId,
      partnerName: data.partnerName,
      partnerTransactionId: data.partnerTransactionId,
      notes: data.notes || '',
      createdBy: data.createdBy || '',
    });
  },

  async upsertPartnerExportEntryByTransactionId(
    partnerTransactionId: string,
    data: {
      month: string;
      coffeeTypeId: string;
      coffeeTypeName: string;
      quantity: number;
      unitPrice: number;
      partnerId: string;
      partnerName: string;
      notes?: string;
      createdBy?: string;
    }
  ): Promise<void> {
    const q = query(
      collection(db, WAREHOUSE_COLLECTION),
      where('partnerTransactionId', '==', partnerTransactionId),
      where('entryType', '==', 'export')
    );
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const existing = snapshot.docs[0];
      await this.updateEntry(existing.id, {
        month: data.month,
        coffeeTypeId: data.coffeeTypeId,
        coffeeTypeName: data.coffeeTypeName,
        unit: 'g',
        quantity: -Math.abs(data.quantity),
        unitPrice: data.unitPrice,
        exportTargetType: 'partner',
        partnerId: data.partnerId,
        partnerName: data.partnerName,
        notes: data.notes || '',
      });
      return;
    }

    await this.addPartnerExportEntry({
      ...data,
      partnerTransactionId,
    });
  },

  async deletePartnerExportByTransactionId(partnerTransactionId: string): Promise<void> {
    const q = query(
      collection(db, WAREHOUSE_COLLECTION),
      where('partnerTransactionId', '==', partnerTransactionId),
      where('entryType', '==', 'export')
    );
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
  },

  // Xóa bản ghi xuất kho liên kết với một phiếu nhập
  async deleteExportByPurchaseItemId(purchaseItemId: string): Promise<void> {
    const q = query(
      collection(db, WAREHOUSE_COLLECTION),
      where('purchaseItemId', '==', purchaseItemId),
      where('entryType', '==', 'export'),
    );
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
  },

  async updateEntry(
    id: string,
    data: Partial<Omit<WarehouseEntry, 'id' | 'createdAt' | 'updatedAt' | 'totalAmount'>>
  ): Promise<void> {
    const quantity = normalizeNumber(data.quantity);
    const unitPrice = normalizeNumber(data.unitPrice);
    const hasPriceInputs = data.quantity !== undefined || data.unitPrice !== undefined;

    const payload: Record<string, any> = {
      ...data,
      updatedAt: Timestamp.fromDate(new Date()),
    };

    if (data.quantity !== undefined) payload.quantity = quantity;
    if (data.unitPrice !== undefined) payload.unitPrice = unitPrice;
    if (data.notes !== undefined) payload.notes = String(data.notes || '').trim();
    if (data.unit !== undefined) payload.unit = String(data.unit || 'g').trim();
    if (data.amountMode !== undefined) payload.amountMode = data.amountMode;

    if (hasPriceInputs) {
      const snap = await getDoc(doc(db, WAREHOUSE_COLLECTION, id));
      const cur = snap.exists() ? snap.data() : {};
      const unitStr =
        data.unit !== undefined
          ? String(data.unit || 'g').trim()
          : String(cur.unit || 'g').trim();
      const q = data.quantity !== undefined ? quantity : normalizeNumber(cur.quantity);
      const p = data.unitPrice !== undefined ? unitPrice : normalizeNumber(cur.unitPrice);
      const mode =
        (data.amountMode as WarehouseAmountMode) ||
        (cur.amountMode as WarehouseAmountMode) ||
        inferAmountMode(unitStr);
      payload.totalAmount = computeTotalAmount(q, p, mode);
      payload.amountMode = mode;
    }

    await updateDoc(doc(db, WAREHOUSE_COLLECTION, id), payload);
  },

  async deleteEntry(id: string): Promise<void> {
    await deleteDoc(doc(db, WAREHOUSE_COLLECTION, id));
  },
};
